// @tennis-matches/core/store/store-adapter
// Store API adapter that maps to Zustand Store
// This allows gradual migration while maintaining backward compatibility

import { useSchleifchenStore } from "./schleifchen/store.js";
import { defaultState } from "../defaults/defaultState.js";

// Internal state for adapter compatibility
let adapterState = null;
/** Monotonic revision for cheap snapshot stability (useSyncExternalStore). */
let stateRevision = 0;
const listeners = new Set();
let zustandUnsubscribe = null;
/** True while adapter→zustand write is in flight (avoids double notify). */
let applyingFromAdapter = false;

/**
 * Wendet nur den Schleifchen-Zustand aus Zustand auf den vollständigen Nested-State an.
 * Wichtig: `shared.settings`, andere Module (z. B. trainerstunden) und übriges `shared.*`
 * bleiben erhalten — reines `zustandToNested` hat diese Felder früher verworfen.
 *
 * @param {object|null|undefined} prevNested
 * @param {object|null|undefined} zustandState
 * @returns {object|null}
 */
function mergeSchleifchenFromZustandInto(prevNested, zustandState) {
  if (!zustandState)
    return prevNested && typeof prevNested === "object"
      ? prevNested
      : structuredClone(defaultState);

  const base =
    prevNested && typeof prevNested === "object"
      ? structuredClone(prevNested)
      : structuredClone(defaultState);

  base.modules ??= {};
  base.shared ??= {};

  const prevSch =
    base.modules.schleifchen && typeof base.modules.schleifchen === "object"
      ? base.modules.schleifchen
      : {};
  const z = zustandState;

  base.modules.schleifchen = {
    ...prevSch,
    tournament: z.tournament || {},
    schedule: z.schedule || [],
    results: z.results || {},
    ui: {
      ...(typeof prevSch.ui === "object" ? prevSch.ui : {}),
      round: z.ui?.currentRound || 1,
      completedRound: z.ui?.completedRound || 0,
      phase: z.ui?.phase || "idle",
      isSaving: z.ui?.isSaving || false,
      isGenerating: z.ui?.isGenerating || false,
      error: z.ui?.error || null,
    },
    participationStatus: z.participationStatus || {},
    resultRules: z.tournament?.resultRules || {},
  };

  base.shared.players = z.players || { count: 12, names: {}, list: [] };

  return base;
}

/**
 * Converts nested format to Zustand store updates
 */
function nestedToZustand(nestedState) {
  if (!nestedState) return null;

  const mod = nestedState?.modules?.schleifchen || {};
  const tournament = mod.tournament || {};
  const schedule = Array.isArray(mod.schedule) ? mod.schedule : [];
  const results = mod.results || {};
  const players = nestedState?.shared?.players || {
    count: 12,
    names: {},
    list: [],
  };
  const ui = mod.ui || {};

  return {
    tournament: {
      ...tournament,
      resultRules: tournament.resultRules || mod.resultRules || {},
    },
    schedule,
    results,
    players,
    ui: {
      currentRound: Number(ui.round || 1),
      completedRound: Number(ui.completedRound || 0),
      phase: ui.phase || "idle",
      isSaving: ui.isSaving || false,
      isGenerating: ui.isGenerating || false,
      error: ui.error || null,
    },
    participationStatus: mod.participationStatus || {},
  };
}

/** Compact fingerprint of the Zustand-facing slice (not the full nested tree). */
function zustandSliceFingerprint(slice) {
  if (!slice) return "";
  try {
    return JSON.stringify(slice);
  } catch {
    return String(Date.now());
  }
}

function ensureAdapterState() {
  if (!adapterState) {
    const zustandState = useSchleifchenStore.getState();
    adapterState = mergeSchleifchenFromZustandInto(null, zustandState);
  }
  return adapterState;
}

/**
 * Notifies all listeners of state changes (one clone shared per notification wave).
 */
function notifyListeners() {
  stateRevision += 1;
  if (!listeners.size) return;
  const current = adapterState ? structuredClone(adapterState) : null;
  for (const listener of listeners) {
    try {
      listener(current);
    } catch (e) {
      console.warn("[store-adapter] Listener error:", e);
    }
  }
}

/**
 * Initializes the store with an initial state.
 * @param {Object} initialState - The initial application state in nested format
 * @returns {void}
 */
export function initStore(initialState) {
  adapterState = initialState;

  if (zustandUnsubscribe) {
    zustandUnsubscribe();
    zustandUnsubscribe = null;
  }

  const zustandUpdate = nestedToZustand(initialState);
  if (zustandUpdate) {
    applyingFromAdapter = true;
    try {
      useSchleifchenStore.setState(zustandUpdate);
    } finally {
      applyingFromAdapter = false;
    }
  }

  let lastZustandFp = zustandSliceFingerprint(zustandUpdate);

  zustandUnsubscribe = useSchleifchenStore.subscribe((zustandState) => {
    if (applyingFromAdapter) {
      // Adapter already holds the intended nested state; keep slice in sync without re-notify.
      return;
    }

    const fp = zustandSliceFingerprint({
      tournament: zustandState.tournament,
      schedule: zustandState.schedule,
      results: zustandState.results,
      players: zustandState.players,
      ui: zustandState.ui,
      participationStatus: zustandState.participationStatus,
    });
    if (fp === lastZustandFp) return;
    lastZustandFp = fp;

    const merged = mergeSchleifchenFromZustandInto(adapterState, zustandState);
    if (!merged) return;
    adapterState = merged;
    notifyListeners();
  });

  stateRevision += 1;
}

/**
 * Cheap change token for React snapshots (no JSON of the full tree).
 * @returns {number}
 */
export function getStateRevision() {
  return stateRevision;
}

/**
 * Gets the current application state in nested format.
 * Always returns a deep copy so callers may mutate the result safely.
 * @returns {Object} The current state object
 */
export function getState() {
  const current = ensureAdapterState();
  return current ? structuredClone(current) : null;
}

/**
 * Updates the application state using an updater function.
 * @param {Function} updater - Function that receives the current state and returns the new state
 * @returns {void}
 */
export function setState(updater) {
  const draft = structuredClone(ensureAdapterState());
  const next = updater(draft);

  adapterState = next;

  const zustandUpdate = nestedToZustand(next);
  if (zustandUpdate) {
    applyingFromAdapter = true;
    try {
      useSchleifchenStore.setState(zustandUpdate);
    } finally {
      applyingFromAdapter = false;
    }
  }

  notifyListeners();
}

/**
 * Subscribes to state changes.
 * Uses the adapter listener list only (Zustand→adapter sync is owned by initStore).
 * @param {Function} listener - Callback function that receives the new state on each change
 * @returns {Function} Unsubscribe function
 */
export function subscribe(listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}
